import React, { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import InputMask from 'react-input-mask';
import * as _ from 'lodash';

const styles = {
  textField: {
    margin: '16px 0 0 0',
    width: '100%',
    height: 40,

    '& input': {
      padding: 12,
      fontSize: 14,
    },
    '& fieldset:disabled': {
      borderColor: '#f44336',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 24px',
    },
    '& fieldset legend span': {
      fontSize: 12,
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
      textTransform: 'none',
      fontWeight: 'normal',
    },
  },
};

const useStyles = makeStyles(styles);

const TextFieldComp = props => {
  const classes = useStyles();
  const {
    docuSignTabMap,
    handleBlur,
    inputProps,
    setReference,
    setReferenceToRequiredFunction,
    setReferenceToErrorsValueFunction,
    mask,
    index,
    type,
    validRegexPattern,
  } = props;
  const [value, setValue] = useState(docuSignTabMap.value ? docuSignTabMap.value : '');
  const [required, setRequired] = useState(docuSignTabMap.required);
  /*
  Errors have the shape:
  { name: string, message: string }[]
  */
  const [errors, setErrors] = useState(docuSignTabMap.errors);

  function hasRegexError(value) {
    if (validRegexPattern) {
      const validRegexPatternActual = new RegExp(validRegexPattern);
      if (
        _.isString(value) &&
        value
          .split('')
          .filter(c => validRegexPatternActual.test(c))
          .join('') !== value
      ) {
        return true;
      }
    }
    return false;
  }

  function doesHaveError(valueToCheck, field) {
    try {
      if (errors && errors.length > 0) {
        return true;
      }
    } catch (e) {
      console.log(`Error checking field.details.error in doesHaveError: ${JSON.stringify(field)}: ${e}`);
    }
    return (
      (!valueToCheck && valueToCheck !== 0 && required) || (_.isString(valueToCheck) && hasRegexError(valueToCheck))
    );
  }

  const [showInputErrorHelperText, setShowInputErrorHelperText] = useState(hasRegexError(docuSignTabMap.value));
  const [hasError, setHasError] = useState(doesHaveError(docuSignTabMap.value, docuSignTabMap));

  const setRequiredValue = newRequiredValue => {
    console.log(`Setting required of ${docuSignTabMap.tabField} to ${newRequiredValue}`);
    setRequired(newRequiredValue);
  };

  const setErrorsValue = errors => {
    setErrors(errors);
  };

  const formatValue = value => {
    let val = value;

    if (val === undefined || val === null || val === '') {
      val = '';
    }

    if (type === 'NUMERICAL') {
      if (inputProps.max && val > inputProps.max) {
        val = inputProps.max;
      }
      if (inputProps.min && val < inputProps.min) {
        val = inputProps.min;
      }
    }

    return val;
  };

  setReference(docuSignTabMap.label, v => {
    setValue(v);
    handleBlur(v, index);
  });
  setReferenceToRequiredFunction(docuSignTabMap.label, setRequiredValue);
  setReferenceToErrorsValueFunction(docuSignTabMap.label, setErrorsValue);

  useEffect(() => {
    if (docuSignTabMap.value !== value) {
      setValue('');
    }
  }, [props]);

  useEffect(() => {
    if (doesHaveError(value, docuSignTabMap)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, [value, required]);

  function doesMaskInputHaveAnError() {
    if (errors && errors.length > 0) {
      return true;
    }
    if (required && (docuSignTabMap.value === null || docuSignTabMap.value === undefined)) {
      return true;
    }
    if (!required && (docuSignTabMap.value === null || docuSignTabMap.value === undefined)) {
      return false;
    }
    if (type === 'PHONE_NUMBER') {
      const phoneNumberLength = docuSignTabMap.value.toString().replace(/[()\-_\s]/g, '').length;
      if (required) {
        return phoneNumberLength !== 10;
      } else {
        return phoneNumberLength !== 10 && phoneNumberLength !== 0;
      }
    }
    if (type === 'SSN') {
      const ssnFeinLength = docuSignTabMap.value.toString().replace(/[-_]/g, '').length;
      if (required) {
        return ssnFeinLength !== 9;
      } else {
        return ssnFeinLength !== 9 && ssnFeinLength !== 0;
      }
    }
    if (hasRegexError(docuSignTabMap.value)) {
      return true;
    }
  }

  let valueToDisplay = value;

  return (
    <Box component="form" noValidate autoComplete="off" key={index} style={{ width: '100%' }}>
      {mask ? (
        <>
          <InputMask
            mask={mask}
            value={valueToDisplay}
            onBlur={() => {
              setValue(valueToDisplay);
              handleBlur(valueToDisplay, index);
            }}
            onChange={e => {
              let val = e.target.value;

              setValue(val);
            }}
          >
            {() => (
              <TextField
                key={index}
                className={classes.textField}
                fullWidth={true}
                label={(required ? '* ' : '') + docuSignTabMap.label}
                style={{ width: '100%' }}
                error={doesMaskInputHaveAnError()}
                variant="outlined"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          </InputMask>
          {errors && errors.length > 0 && (
            <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.map(e => e.message).join(', ')}</div>
          )}
        </>
      ) : (
        <div>
          <TextField
            key={index}
            className={classes.textField}
            fullWidth={true}
            label={(required ? '* ' : '') + docuSignTabMap.label}
            value={valueToDisplay}
            onBlur={() => {
              let formattedValue = formatValue(value, type);
              setValue(formattedValue);
              handleBlur(formattedValue, index);
            }}
            style={{ width: '100%' }}
            onChange={e => {
              let val = e.target.value;

              if (type === 'NUMERICAL') {
                const numVal = parseFloat(val);
                if (numVal && !isNaN(numVal)) {
                  if (inputProps.max && numVal > inputProps.max) {
                    val = inputProps.max.toString();
                  } else if (inputProps.min && numVal < inputProps.min) {
                    val = inputProps.min.toString();
                  }
                }
              }

              if ((type !== 'TEXT' && parseInt(val) < 0) || (type === 'YEAR' && parseInt(val) > 9999)) {
                val = docuSignTabMap.value;
              }

              if (type === 'TEXT') {
                if (inputProps.maxLength && val.length > inputProps.maxLength) {
                  val = val.slice(0, inputProps.maxLength);
                }
              }

              if (type === 'INITIALS') {
                val = val.toUpperCase();
              }

              if (validRegexPattern) {
                const validRegexPatternActual = new RegExp(validRegexPattern);
                val = val
                  .split('')
                  .filter(char => validRegexPatternActual.test(char))
                  .join('');
              }

              setValue(val);
            }}
            variant="outlined"
            error={hasError}
            slotProps={{
              htmlInput:
                (type !== 'TEXT' && {
                  type: type === 'NUMERICAL' ? 'number' : 'string',
                  step: type === 'NUMERICAL' ? 'any' : '1',
                  min: '1',
                  max: type === 'YEAR' ? '9999' : undefined,
                },
                { ...inputProps }),
              inputLabel: {
                shrink: true,
              },
            }}
          />
          <div>
            {/* {showInputErrorHelperText && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{field.details?.inputErrorHelperText}</div>
            )} */}
            {errors && errors.length > 0 && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.map(e => e.message).join(', ')}</div>
            )}
          </div>
        </div>
      )}
    </Box>
  );
};

export default TextFieldComp;
